import type { NavigateFunction } from "react-router-dom";
import patientRequest from "@/api/patient-request";
import taskRequest from "@/api/task-request";
import { useTaskStore } from "@/store/task";
import { type TaskItem, isFormTask } from "@/types/tasks";
import type { CaseHistoryTaskAction } from "@/types/task-actions";
import { DEFAULT_PORTAL_CONFIG_LIMITS, parsePortalConfigMeta } from "@/types/task-actions";
import {
  buildFormHandlerLaunchPayload,
  buildFormHandlerLaunchPayloadFromAction,
} from "@/types/portal-forms";
import { navigateToFormHandler } from "@/utils/formHandlerLaunch";
import { SESSION_PORTAL_CONFIG_RESET_PENDING } from "@/constants/portalConfigSession";
import { buildInsuranceWizardSteps, buildWizardSteps } from "@/hooks/useWizardFlow";
import { getIntakeFlagsFor, resolveIntakeEntryPath } from "@/utils/intakeResume";
import { toast } from "sonner";
import {
  isPortalHistorySessionLocked,
  PORTAL_SESSION_LOCKED_PATIENT_MESSAGE,
} from "@/utils/portalSessionLock";

/**
 * Opens a portal task using the same routing and store updates as the dashboard task list.
 * Used by DashTask and post-login task resume from email links.
 */
export async function openPortalTask(params: {
  navigate: NavigateFunction;
  facilityKey: string;
  patientKey: string;
  task: TaskItem;
  allTasks: TaskItem[];
}): Promise<void> {
  const { navigate, facilityKey, patientKey, task, allTasks } = params;
  const {
    setCaseHistoryTaskAction,
    setActivePortalTaskId,
    setPortalCaseHistoryFlow,
    setFvTaskAction,
    setTaskCode,
    setTaskItem,
    setTaskInfo,
    setCurrentCaseHistoryId,
    setCurrentTaskType,
    setPortalConfigMeta,
    setWizardSteps,
  } = useTaskStore.getState();

  setCaseHistoryTaskAction(null);
  setActivePortalTaskId(null);
  setPortalCaseHistoryFlow(false);
  setFvTaskAction(null);
  setPortalConfigMeta(null);
  setWizardSteps([]);

  const taskType = (task.taskType ?? "").toUpperCase();

  if (task.isHistory) {
    setTaskCode(task.taskType);
    setTaskItem(task);

    let action: CaseHistoryTaskAction | undefined;
    try {
      const actionRes = await taskRequest.request.getCaseHistoryTaskAction(
        facilityKey,
        patientKey,
        task.taskID
      );
      action = actionRes?.data as CaseHistoryTaskAction | undefined;
      if (action) setCaseHistoryTaskAction(action);
    } catch {
      /* task action unavailable */
    }

    setActivePortalTaskId(task.taskID);
    setPortalCaseHistoryFlow(true);
    try {
      sessionStorage.setItem(SESSION_PORTAL_CONFIG_RESET_PENDING, "1");
    } catch {
      /* ignore */
    }
    if (task.info) {
      try {
        const info = typeof task.info === "string" ? JSON.parse(task.info) : task.info;
        const caseHistoryId = info?.CaseHistoryId ?? info?.caseHistoryId ?? info?.caseHistoryID;
        if (caseHistoryId != null) {
          const numericCaseHistoryId = Number(caseHistoryId);
          setCurrentCaseHistoryId(numericCaseHistoryId);
          if (numericCaseHistoryId > 0) {
            try {
              const configsRes = await taskRequest.request.getCaseHistoryPortalConfigs(
                facilityKey,
                patientKey,
                numericCaseHistoryId
              );
              if (isPortalHistorySessionLocked(configsRes?.data ?? [])) {
                toast.error(PORTAL_SESSION_LOCKED_PATIENT_MESSAGE);
                navigate("/", { replace: true });
                return;
              }
            } catch {
              /* proceed if lock check unavailable */
            }
          }
        }
      } catch {
        /* ignore */
      }
    }

    // General History (typeId === 1) is rendered as its own config step; ordering/behaviors come from the
    // driving custom config (first non-general), so parse meta from that one.
    const allConfigs = action?.caseHistoryConfigs ?? [];
    const hasGeneralConfig = allConfigs.some((c) => Number(c.typeId) === 1);
    const hasCustomConfig = allConfigs.some((c) => Number(c.typeId) !== 1);
    const drivingConfig =
      allConfigs.find((c) => Number(c.typeId) !== 1) ?? allConfigs[0] ?? null;
    const portalConfigCode = drivingConfig?.portalConfigCode?.trim() ?? "";

    let meta = null;
    if (portalConfigCode) {
      try {
        const cfgRes = await taskRequest.request.getPortalConfigData(facilityKey, portalConfigCode);
        const raw = cfgRes?.data;
        const text = typeof raw === "string" ? raw : typeof raw === "object" ? JSON.stringify(raw) : "{}";
        const doc = JSON.parse(text) as Record<string, unknown>;
        meta = parsePortalConfigMeta(doc);
      } catch {
        /* config unavailable — fall back to a default meta below so configs still render */
      }
    }
    // Configs present but meta unavailable → synthesize a default so the role-driven flow still runs.
    if (!meta && (hasCustomConfig || hasGeneralConfig)) {
      meta = {
        id: "",
        name: drivingConfig?.description?.trim() || "History",
        complaintBehavior: "include" as const,
        generalBehavior: hasGeneralConfig ? ("include" as const) : ("hide" as const),
        position: "before_complaint" as const,
        // A config drives this flow even though its JSON couldn't be read, so its limits default.
        ...DEFAULT_PORTAL_CONFIG_LIMITS,
      };
    }
    setPortalConfigMeta(meta);

    const hasForms = allTasks.some((t) => isFormTask(t.taskType));
    // Drop already-completed leading steps and resume at the first unfinished one.
    const progress = getIntakeFlagsFor(task.taskID);
    const steps = buildWizardSteps(meta, hasForms, { hasCustomConfig, hasGeneralConfig }, progress);
    setWizardSteps(steps);

    navigate(await resolveIntakeEntryPath({ taskId: task.taskID, wizardSteps: steps }));
    return;
  }

  setTaskCode(task.taskType);
  switch (taskType) {
    case "NPH":
      setTaskItem(task);
      if (task.info) {
        try {
          const info = typeof task.info === "string" ? JSON.parse(task.info) : task.info;
          const caseHistoryId = info?.CaseHistoryId ?? info?.caseHistoryId ?? info?.caseHistoryID;
          if (caseHistoryId != null) setCurrentCaseHistoryId(Number(caseHistoryId));
        } catch {
          /* ignore */
        }
      }
      navigate("/complaint/demographics");
      break;
    case "INS": {
      // Insurance only: Demographics → Comm Prefs → financial / insurance → complete. No history.
      setTaskItem(task);
      setActivePortalTaskId(task.taskID);
      // The financial pages hand off along the planned steps when this is set; the plan here
      // ends at the insurance completion step instead of a history.
      setPortalCaseHistoryFlow(true);
      const steps = buildInsuranceWizardSteps(getIntakeFlagsFor(task.taskID));
      setWizardSteps(steps);
      navigate(await resolveIntakeEntryPath({ taskId: task.taskID, wizardSteps: steps }));
      break;
    }
    case "AANPH":
    case "AACH":
      setTaskItem(task);
      if (task.info) {
        try {
          const info = typeof task.info === "string" ? JSON.parse(task.info) : task.info;
          const caseHistoryId = info?.CaseHistoryId ?? info?.caseHistoryId ?? info?.caseHistoryID;
          if (caseHistoryId != null) setCurrentCaseHistoryId(Number(caseHistoryId));
          setTaskInfo({
            ...info,
            TaskType: task.taskType,
            TaskID: task.taskID,
          });
        } catch {
          setTaskInfo("");
        }
      } else {
        setTaskInfo("");
      }
      setCurrentTaskType(task.taskType);
      navigate("/complaint/demographics");
      break;
    case "CH":
      setTaskInfo("");
      setTaskItem(task);
      if (task.info) {
        try {
          const info = typeof task.info === "string" ? JSON.parse(task.info) : task.info;
          const caseHistoryId = info?.CaseHistoryId ?? info?.caseHistoryId ?? info?.caseHistoryID;
          if (caseHistoryId != null) setCurrentCaseHistoryId(Number(caseHistoryId));
        } catch {
          /* ignore */
        }
      }
      setCurrentTaskType(task.taskType);
      navigate("/complaint/demographics");
      break;
    case "STMT":
      // StatementPage loads the statement itself, scoped to the current patient.
      navigate(`/menu/statement?taskId=${task.taskID}`);
      break;
    case "FORM": {
      const formTaskCount = allTasks.filter((t) => isFormTask(t.taskType)).length;
      try {
        const formRes = await taskRequest.request.getFormTaskAction(
          facilityKey,
          patientKey,
          task.taskID
        );
        const action = formRes?.data;
        if (action && typeof action.facilityFormID === "number" && !action.error) {
          const payload = buildFormHandlerLaunchPayloadFromAction(
            task,
            action,
            formTaskCount
          );
          if (payload) {
            navigateToFormHandler(navigate, payload);
            break;
          }
        }
      } catch {
        /* fall back */
      }
      try {
        const payload = buildFormHandlerLaunchPayload(task, formTaskCount);
        if (payload) navigateToFormHandler(navigate, payload);
      } catch {
        // ignore
      }
      break;
    }
    case "FV": {
      try {
        const fvRes = await taskRequest.request.getFvTaskAction(
          facilityKey,
          patientKey,
          task.taskID
        );
        setTaskItem(task);
        setFvTaskAction(fvRes?.data ?? null);
        setActivePortalTaskId(task.taskID);
        navigate("/task/fv");
      } catch (e) {
        console.error("GetFvTaskAction:", e);
        setCurrentTaskType(task.taskType);
        navigate("/");
      }
      break;
    }
    default:
      setCurrentTaskType(task.taskType);
      navigate("/");
  }
}

/**
 * Loads tasks for the patient and opens the task matching taskId, if present.
 * @returns true if a task was opened, false if navigated home as fallback.
 */
export async function openPortalTaskById(params: {
  navigate: NavigateFunction;
  facilityKey: string;
  patientKey: string;
  taskId: number;
}): Promise<boolean> {
  const { navigate, facilityKey, patientKey, taskId } = params;
  if (taskId <= 0) {
    navigate("/", { replace: true });
    return false;
  }
  try {
    const res = await patientRequest.request.gettasks(facilityKey, patientKey);
    const tasks = (res?.data as TaskItem[]) ?? [];
    const task = tasks.find((t) => t.taskID === taskId);
    if (!task) {
      navigate("/", { replace: true });
      return false;
    }
    await openPortalTask({ navigate, facilityKey, patientKey, task, allTasks: tasks });
    return true;
  } catch {
    navigate("/", { replace: true });
    return false;
  }
}
